import { useEffect, useState } from 'react';

import type { FieldActorId, FieldSnapshot } from '@mio/shared';

const key = 'mio.field.read-messages';
type ReadState = Record<string, string[]>;
function storedReads(): ReadState {
  try {
    const value: unknown = JSON.parse(localStorage.getItem(key) ?? '{}');
    if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
    return Object.fromEntries(
      Object.entries(value).filter(
        ([, ids]) => Array.isArray(ids) && ids.every((id: unknown) => typeof id === 'string'),
      ),
    );
  } catch {
    return {};
  }
}

/** Read indicators are local to this browser and never imply a colleague read receipt. */
export function useFieldReadState(
  snapshot: FieldSnapshot | undefined,
  activeActor: FieldActorId | null,
) {
  const [read, setRead] = useState<ReadState>(storedReads);
  const [tabVisible, setTabVisible] = useState(() => document.visibilityState !== 'hidden');
  useEffect(() => {
    const update = () => setTabVisible(document.visibilityState !== 'hidden');
    document.addEventListener('visibilitychange', update);
    return () => document.removeEventListener('visibilitychange', update);
  }, []);
  useEffect(() => {
    if (!snapshot || !activeActor || !tabVisible) return;
    const seen = new Set(read[snapshot.engagement.id] ?? []);
    const messages = snapshot.messages.filter(
      (message) =>
        !message.meetingId && message.role === 'colleague' && message.actorId === activeActor,
    );
    if (messages.every((message) => seen.has(message.id))) return;
    messages.forEach((message) => seen.add(message.id));
    const next = { ...read, [snapshot.engagement.id]: [...seen] };
    setRead(next);
    try {
      localStorage.setItem(key, JSON.stringify(next));
    } catch {
      /* Reads remain available in this tab. */
    }
  }, [snapshot, activeActor, read, tabVisible]);
  const seen = new Set(snapshot ? (read[snapshot.engagement.id] ?? []) : []);
  const unreadByActor: Partial<Record<FieldActorId, number>> = {};
  for (const message of snapshot?.messages ?? []) {
    if (!message.meetingId && message.role === 'colleague' && !seen.has(message.id)) {
      unreadByActor[message.actorId] = (unreadByActor[message.actorId] ?? 0) + 1;
    }
  }
  return {
    unreadByActor,
    unreadCount: Object.values(unreadByActor).reduce((sum, value) => sum + value, 0),
  };
}
